import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { CounselingsService, RequestHelp, RequestHelpService } from '../../../core/api';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { getOfferState } from '../../../core/states/offer/offer.selector';
import { OfferState } from '../../../core/states/offer/offer.state';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { UtmService } from '../../../core/services/utm.service';
import { ApiLanguageService } from '../../../core/services/language/api-language.service';
import { GenesysService } from '../../../core/services/genesys.service';
import { getVisibilityOfCustomerChat } from '../../../core/states/app/app.selector';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-contact-overlay-content',
  templateUrl: './contact-overlay-content.component.html',
  styleUrls: ['./contact-overlay-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactOverlayContentComponent {
  @Input() public title?: string;
  @Input() public padded = false;

  public showChat$: Observable<boolean>;
  public showLoadingSpinner: boolean = false;
  public chatReady: boolean = false;

  constructor(
    private counselingService: CounselingsService,
    private requestHelpService: RequestHelpService,
    private store: Store,
    private apiLanguageService: ApiLanguageService,
    private utmService: UtmService,
    private genesysService: GenesysService,
    private cdr: ChangeDetectorRef,
  ) {
    this.showChat$ = combineLatest([
      this.genesysService.chatAvailableInLanguage$(),
      store.select(getVisibilityOfCustomerChat),
    ]).pipe(
      map(
        ([chatIsAvailableInCurrentLanguage, chatFeatureFlagIsEnabled]) =>
          chatIsAvailableInCurrentLanguage && chatFeatureFlagIsEnabled,
      ),
    );
  }

  public scheduleMeeting(): void {
    this.counselingService
      .getCounseling()
      .pipe(untilDestroyed(this))
      .subscribe((counseling) => {
        window.location.href = counseling.url;
      });
  }

  public createMessage(): void {
    this.store
      .select(getOfferState)
      .pipe(
        untilDestroyed(this),
        switchMap((offerState) => this.requestHelpService.requestHelp(this.mapRequestHelp(offerState))),
      )
      .subscribe((requestHelpUrl) => {
        window.location.href = requestHelpUrl.url;
      });
  }

  private mapRequestHelp(offerState: OfferState): RequestHelp {
    const currentLanguage = this.apiLanguageService.getCurrentRequestLanguageForApi();
    if (offerState.persons[0] === undefined) {
      return {
        gender: 'MALE',
        request_language: currentLanguage,
        utm_parameters: this.utmService.getUTMParameters(),
      };
    }

    const { details: personDetails } = offerState.persons[0];
    return {
      first_name: personDetails.firstName,
      zip_code: offerState.address?.zipCode,
      date_of_birth: personDetails.dateOfBirth,
      gender:
        personDetails.genderOrPrenatal === 'MALE' || personDetails.genderOrPrenatal === 'PRENATAL' ? 'MALE' : 'FEMALE',
      request_language: currentLanguage,
      utm_parameters: this.utmService.getUTMParameters(),
    };
  }

  public toggleMessenger(): void {
    if (this.chatReady) {
      this.genesysService.openChat();
      return;
    }

    this.showLoadingSpinner = true;
    this.genesysService.loadGenesysScript();

    this.genesysService
      .chatReady$()
      .pipe(untilDestroyed(this))
      .subscribe({
        complete: () => {
          this.chatReady = true;
          this.showLoadingSpinner = false;
          this.cdr.detectChanges();
          this.genesysService.openChat();
        },
      });
  }
}
